import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import PageHeader from '@/components/PageHeader';
import ComplianceBanner from '@/components/ComplianceBanner';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { BookOpen, Search, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function SOPListPage() {
  const { isManager } = useAuth();
  const [sops, setSops] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ title: '', purpose: '', responsible: 'Operations', steps: '' });

  const load = async (query = '') => {
    setLoading(true);
    try {
      const res = await api.get('/sops' + (query ? `?q=${encodeURIComponent(query)}` : ''));
      setSops(res.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q) return sops;
    const term = q.toLowerCase();
    return sops.filter((s) =>
      s.title.toLowerCase().includes(term) ||
      s.purpose.toLowerCase().includes(term) ||
      (s.responsible || '').toLowerCase().includes(term)
    );
  }, [q, sops]);

  const createSOP = async () => {
    const stepsArr = form.steps.split('\n').map((x) => x.trim()).filter(Boolean);
    await api.post('/sops', { title: form.title, purpose: form.purpose, responsible: form.responsible, steps: stepsArr });
    setCreateOpen(false);
    setForm({ title: '', purpose: '', responsible: 'Operations', steps: '' });
    load();
  };

  return (
    <div>
      <PageHeader
        icon={BookOpen}
        title="SOP Library"
        subtitle="Search, follow, and update standard operating procedures."
        actions={isManager && (
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-sop-btn" className="bg-amber-500 hover:bg-amber-400 text-[#0a1628] font-semibold">
                <Plus className="h-4 w-4 mr-1" /> New SOP
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0a1628] border-amber-500/20 text-slate-100 max-w-lg">
              <DialogHeader><DialogTitle className="text-white">Create new SOP</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-[#0f2240] border-amber-500/20" /></div>
                <div><Label>Purpose</Label><Textarea value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} className="bg-[#0f2240] border-amber-500/20" /></div>
                <div><Label>Responsible</Label><Input value={form.responsible} onChange={(e) => setForm({ ...form, responsible: e.target.value })} className="bg-[#0f2240] border-amber-500/20" /></div>
                <div><Label>Steps (one per line)</Label><Textarea rows={6} value={form.steps} onChange={(e) => setForm({ ...form, steps: e.target.value })} className="bg-[#0f2240] border-amber-500/20" /></div>
              </div>
              <DialogFooter><Button onClick={createSOP} className="bg-amber-500 hover:bg-amber-400 text-[#0a1628]">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />

      <ComplianceBanner compact />

      <div className="mt-6 mb-4 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-amber-300/70" />
        <Input
          data-testid="sop-search"
          placeholder="Search by title, purpose, or responsible role…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-9 bg-[#0f2240] border-amber-500/20 text-white placeholder:text-slate-500"
        />
      </div>

      {loading ? (
        <div className="text-slate-400">Loading…</div>
      ) : filtered.length === 0 ? (
        <Card className="shine-card p-10 text-center text-slate-400 rounded-xl">No SOPs match your search.</Card>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {filtered.map((s) => (
            <Link key={s.id} to={`/sops/${s.id}`} className="group" data-testid={`sop-card-${s.id}`}>
              <Card className="shine-card p-4 rounded-xl hover:border-amber-400/50 transition">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-bold text-white">{s.title}</h3>
                    <p className="text-xs text-slate-400 mt-1 line-clamp-2">{s.purpose}</p>
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                      <Badge variant="outline" className="border-amber-500/40 text-amber-200 text-[10px]">{s.responsible}</Badge>
                      <span className="text-[10px] text-slate-500">{(s.steps || []).length} steps</span>
                      <span className="text-[10px] text-slate-500">v{(s.versions || []).length || 1}</span>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-amber-300/60 group-hover:translate-x-1 transition flex-shrink-0" />
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
